<p-toast [life]="5000"></p-toast>

<app-page-shell>
    <app-page-hero eyebrow="Accounting Setup" title="Cost Centers"
        subtitle="Manage reporting dimensions for branch, warehouse, route, department, and operations analysis.">
        <div actions>
            <p-button *ngIf="canAddCostCenter" label="New Cost Center" icon="pi pi-plus" (onClick)="openNew()"></p-button>
        </div>
    </app-page-hero>

    <p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

    <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

    <app-page-data-panel title="Cost Center Directory"
        subtitle="Accounting dimensions available for allocation, posting review, and reporting."
        [resultText]="costCenters.length + ' configured'">
        <div actions>
            <span class="search-field">
                <i class="pi pi-search"></i>
                <input pInputText type="text" (input)="applyFilterGlobal($event, 'contains')"
                    placeholder="Search cost centers" />
            </span>
        </div>

        <p-table #dt [value]="costCenters" [paginator]="true" [rows]="10" [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            [rowsPerPageOptions]="[10,25,50]" [globalFilterFields]="['code','name','description']"
            styleClass="p-datatable-gridlines cost-center-table" [tableStyle]="{ 'min-width': '48rem' }">
            <ng-template pTemplate="header">
                <tr>
                    <th pSortableColumn="code">Cost Center <p-sortIcon field="code"></p-sortIcon></th>
                    <th pSortableColumn="description">Allocation Context <p-sortIcon field="description"></p-sortIcon></th>
                    <th pSortableColumn="isActive" class="status-col">Status <p-sortIcon field="isActive"></p-sortIcon></th>
                    <th class="actions-col">Actions</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-costCenter>
                <tr>
                    <td>
                        <div class="cell-stack">
                            <span class="cost-center-code">{{ costCenter.code }}</span>
                            <strong>{{ costCenter.name }}</strong>
                        </div>
                    </td>
                    <td>
                        <div class="cell-stack">
                            <span>{{ costCenter.description || 'No description provided' }}</span>
                            <span class="cell-meta">
                                <i class="pi pi-sitemap"></i>
                                Reporting dimension
                            </span>
                        </div>
                    </td>
                    <td class="status-col">
                        <p-tag [value]="costCenter.isActive === false ? 'Inactive' : 'Active'"
                            [severity]="statusSeverity(costCenter)"></p-tag>
                    </td>
                    <td class="actions-col">
                        <p-button *ngIf="canEditCostCenter" icon="pi pi-pencil" severity="info" [text]="true"
                            [rounded]="true" pTooltip="Edit cost center" (onClick)="editCostCenter(costCenter)"></p-button>
                        <p-button *ngIf="canDeleteCostCenter" icon="pi pi-trash" severity="danger" [text]="true"
                            [rounded]="true" pTooltip="Delete cost center"
                            (onClick)="confirmDelete(costCenter)"></p-button>
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="4" class="empty-cell">No cost centers found.</td>
                </tr>
            </ng-template>
        </p-table>
    </app-page-data-panel>
</app-page-shell>

<p-dialog [(visible)]="costCenterDialog" [style]="{ width: 'min(42rem, 96vw)' }" [header]="dialogTitle" [modal]="true"
    styleClass="cost-center-dialog">
    <ng-template pTemplate="content">
        <form [formGroup]="costCenterForm" class="cost-center-form">
            <p class="dialog-subtitle">{{ dialogSubtitle }}</p>

            <div class="form-grid">
                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon>
                            <i class="pi pi-hashtag"></i>
                        </p-inputGroupAddon>
                        <p-iftaLabel>
                            <input id="code" type="text" pInputText formControlName="code" />
                            <label for="code">Code</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                    @if (costCenterForm.get('code')?.invalid && costCenterForm.get('code')?.touched) {
                    <small class="p-error">
                        @if (costCenterForm.get('code')?.hasError('required')) {
                        Code is required.
                        }
                        @if (costCenterForm.get('code')?.hasError('maxlength')) {
                        Code cannot exceed 20 characters.
                        }
                    </small>
                    }
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon>
                            <i class="pi pi-tag"></i>
                        </p-inputGroupAddon>
                        <p-iftaLabel>
                            <input id="name" type="text" pInputText formControlName="name" />
                            <label for="name">Name</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                    @if (costCenterForm.get('name')?.invalid && costCenterForm.get('name')?.touched) {
                    <small class="p-error">
                        @if (costCenterForm.get('name')?.hasError('required')) {
                        Name is required.
                        }
                        @if (costCenterForm.get('name')?.hasError('maxlength')) {
                        Name cannot exceed 100 characters.
                        }
                    </small>
                    }
                </div>
            </div>

            <div class="field">
                <p-iftaLabel>
                    <textarea id="description" pInputTextarea formControlName="description" rows="4"
                        [autoResize]="true"></textarea>
                    <label for="description">Description</label>
                </p-iftaLabel>
            </div>

            <label class="active-toggle" for="isActive">
                <p-checkbox id="isActive" formControlName="isActive" [binary]="true"></p-checkbox>
                <span>
                    <strong>Active</strong>
                    <small>Allow this cost center to be used in finance posting and reporting screens.</small>
                </span>
            </label>
        </form>
    </ng-template>

    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [text]="true" (onClick)="hideDialog()"></p-button>
        <p-button label="Save" icon="pi pi-check" (onClick)="saveCostCenter()"
            [disabled]="costCenterForm.invalid || !canSaveCostCenter"></p-button>
    </ng-template>
</p-dialog>

<p-confirmDialog [style]="{ width: 'min(28rem, 96vw)' }"></p-confirmDialog>
